/**
 * Shared Telstra utility functions for formatting and calculations
 */

/**
 * Format a number as Australian currency
 */
export const formatCurrency = (amount: number): string => {
  const safeAmount = Number.isFinite(amount) ? amount : 0;
  return new Intl.NumberFormat('en-AU', {
    style: 'currency',
    currency: 'AUD',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2
  }).format(safeAmount);
};

/**
 * Convert megabytes to gigabytes
 */
export const mbToGb = (mb: number): number => mb / 1024;

/**
 * Format gigabytes for display (e.g., "2.5GB")
 */
export const formatGb = (gb: number): string => {
  const rounded = Math.round(gb * 10) / 10;
  return `${rounded.toFixed(1)}GB`;
};

/**
 * Format data usage from MB to a human-readable string
 */
export const formatDataUsage = (mb: number): string => {
  if (mb < 1024) {
    return `${Math.round(mb)}MB`;
  }
  return formatGb(mbToGb(mb));
};

/**
 * Calculate usage percentage from used and allowance values
 */
export const calculateUsagePercentage = (used: number, allowance: number): number => {
  if (allowance <= 0 || !Number.isFinite(allowance)) return 0;
  const percentage = (used / allowance) * 100;
  return Math.max(0, Math.min(100, Math.round(percentage)));
};

/**
 * Format a phone number for display (Australian format)
 */
export const formatPhoneNumber = (phone: string): string => {
  const cleaned = phone.replace(/\s/g, '');
  // Format as XXXX XXX XXX for Australian mobiles
  if (cleaned.length === 10 && cleaned.startsWith('0')) {
    return `${cleaned.slice(0, 4)} ${cleaned.slice(4, 7)} ${cleaned.slice(7)}`;
  }
  return phone;
};

/**
 * Get color class for usage percentage (for progress bars)
 */
export const getUsageColorClass = (percentage: number): string => {
  if (percentage >= 90) return 'bg-red-500';
  if (percentage >= 75) return 'bg-orange-500';
  if (percentage >= 50) return 'bg-yellow-500';
  return 'bg-primary-500';
};

/**
 * Parse a currency string to a number
 */
export const parseCurrency = (value: string): number => {
  const cleaned = value.replace(/[^0-9.-]/g, '');
  const parsed = parseFloat(cleaned);
  return Number.isFinite(parsed) ? parsed : 0;
};

/**
 * Format a date for display
 */
export const formatStatementMonth = (yearMonth: string): string => {
  // Expects YYYY-MM format
  const [year, month] = yearMonth.split('-');
  if (!year || !month) return yearMonth;
  
  const date = new Date(parseInt(year), parseInt(month) - 1);
  return date.toLocaleDateString('en-AU', { year: 'numeric', month: 'long' });
};

/**
 * Chart color palette for Telstra visualizations
 */
export const CHART_COLORS = {
  primary: ['#3b82f6', '#60a5fa', '#93c5fd', '#bfdbfe', '#dbeafe'],
  success: ['#22c55e', '#4ade80', '#86efac', '#bbf7d0'],
  warning: ['#f59e0b', '#fbbf24', '#fcd34d', '#fde68a'],
  danger: ['#ef4444', '#f87171', '#fca5a5', '#fecaca'],
  neutral: ['#6b7280', '#9ca3af', '#d1d5db', '#e5e7eb', '#f3f4f6']
};

/**
 * Get a color from the palette by index (cycles through)
 */
export const getChartColor = (index: number, palette: string[] = CHART_COLORS.primary): string => {
  return palette[index % palette.length];
};

/**
 * Calculate month-over-month change percentage
 */
export const calculateChangePercentage = (current: number, previous: number): number => {
  if (previous === 0) return current > 0 ? 100 : 0;
  return Math.round(((current - previous) / previous) * 100);
};

/**
 * Format a change value with sign and color indicator
 */
export const formatChange = (current: number, previous: number): { 
  value: string; 
  percentage: number;
  isPositive: boolean;
  isNegative: boolean;
} => {
  const diff = current - previous;
  const percentage = calculateChangePercentage(current, previous);
  return {
    value: formatCurrency(Math.abs(diff)),
    percentage,
    isPositive: diff > 0,
    isNegative: diff < 0
  };
};

